/**
 * "Use the paper" — the job-page rip cord (Issue #1300).
 *
 * The tech tested the device and signed the form; the app workflow never
 * finished. This records the PAPER as the device's result so the customer gets
 * a clean report and the device's clock moves.
 *
 * It is deliberately not a way to author a test. The office picks the signed
 * form already on the job, says what it says, and names the licensed tester who
 * signed it — the readings stay on the page the tester signed, which becomes
 * the customer's copy. Reason is required: a conversion with no stated reason
 * is indistinguishable from someone routing around the workflow because it was
 * inconvenient.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSignature, Loader2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { apiErrorInfo, techLabel, type JobAssetRow, type TechOption } from "./types";
import { HowItWorks } from "@/components/how-it-works";

interface JobAttachmentOption {
    id: string;
    file_name: string;
    created_at: string;
}

/** Local-date default (not UTC): a test signed last night must not offer today. */
export function todayInputValue(now: Date = new Date()): string {
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${mm}-${dd}`;
}

/**
 * A `YYYY-MM-DD` from the date input, as noon LOCAL time in ISO form.
 *
 * Noon, not midnight, on purpose: midnight local in a west-of-UTC zone lands on
 * the PREVIOUS day once converted, which would date the compliance clock — and
 * the customer's report — one day early. Noon is safely inside the day in every
 * US zone.
 */
export function dateInputToIso(value: string): string | null {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

export type PaperResult = "pass" | "fail";

/** "2026-09-24" → "9/24/2026" (how the form itself writes it). */
export function formatFormDate(iso: string): string {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : iso;
}

/** What the server read off the picked form (GET …/paper-conversion/evidence/:id). */
export interface EvidenceReading {
    namesDevices: Array<{ assetId: string; serial: string | null }>;
    matchesDevice: boolean;
    formDate: string | null;
    formResult: PaperResult | null;
}

/**
 * The job's files with THIS device's form first (Litsey Creek #092678280: 58
 * files, and the office picked S/N 582266's form for 580176). A file "names"
 * the device when its serial is a whole token of the name, ignoring dashes,
 * spaces and leading zeros.
 */
export function namesSerial(fileName: string, serial: string | null | undefined): boolean {
    if (!serial) return false;
    const norm = (v: string) =>
        v
            .toUpperCase()
            .replace(/[\s-]/g, "")
            .replace(/^0+(?=.)/, "");
    const want = norm(serial);
    if (want.length < 3) return false;
    const base = fileName.replace(/\.[a-z0-9]{2,4}$/i, "").toUpperCase();
    return base
        .split(/[^A-Z0-9-]+/)
        .flatMap((t) => [t, t.replace(/^S-?N-?(?=[A-Z0-9])/, "")])
        .some((t) => t.length >= 3 && norm(t) === want);
}

export function sortEvidenceFiles<T extends { file_name: string }>(files: T[], serial: string | null | undefined): T[] {
    return [...files].sort(
        (a, b) => Number(namesSerial(b.file_name, serial)) - Number(namesSerial(a.file_name, serial)),
    );
}

/**
 * Whether the conversion can be saved. The result has NO default: on
 * #072658274 the toggle sat on Passed while the reason said "still FAILED", so
 * a failed device was saved as a pass and its next-test date jumped a year.
 * The office has to read the form and pick one.
 */
export function canRecordPaper(input: {
    hasAsset: boolean;
    result: PaperResult | null;
    evidenceId: string;
    reason: string;
    dateValid: boolean;
    pending: boolean;
    /** False while the picked form names another device and the office hasn't
     *  said it covers this one too. Omitted/true = fine. */
    otherDeviceOk?: boolean;
}): boolean {
    return (
        input.hasAsset &&
        input.result !== null &&
        !!input.evidenceId &&
        input.reason.trim().length > 0 &&
        input.dateValid &&
        !input.pending &&
        input.otherDeviceOk !== false
    );
}

export function PaperConversionDialog({
    open,
    onClose,
    jobId,
    asset,
    techs,
    defaultTesterId,
    onConverted,
}: {
    open: boolean;
    onClose: () => void;
    jobId: string;
    asset: JobAssetRow | null;
    techs: TechOption[];
    /** The job's assigned tech — the signer in the overwhelming majority. */
    defaultTesterId?: string | null;
    onConverted?: () => void;
}) {
    const qc = useQueryClient();
    const [result, setResult] = useState<PaperResult | null>(null);
    const [performedOn, setPerformedOn] = useState(todayInputValue());
    const [testerUserId, setTesterUserId] = useState<string>("");
    const [evidenceId, setEvidenceId] = useState<string>("");
    const [reason, setReason] = useState("");
    const [dateTouched, setDateTouched] = useState(false);
    const [coversBoth, setCoversBoth] = useState(false);
    const [cantFile, setCantFile] = useState(false);

    // Every non-archived file on the job is a candidate: the signed form can
    // arrive as a dropped scan (`document`), a phone photo, or an already-
    // categorised report. Narrowing the list by category is how the office
    // ends up unable to find the page it is looking at.
    const filesQ = useQuery({
        queryKey: ["job-attachments-all", jobId],
        enabled: open,
        queryFn: () => api.get<{ attachments: JobAttachmentOption[] }>(`/api/attachments?jobId=${jobId}`),
    });
    // This device's form first; picked for the office when exactly one file names it.
    const files = useMemo(
        () => sortEvidenceFiles(filesQ.data?.attachments ?? [], asset?.serial_number),
        [filesQ.data, asset?.serial_number],
    );
    useEffect(() => {
        if (!open || evidenceId) return;
        const own = files.filter((f) => namesSerial(f.file_name, asset?.serial_number));
        if (own.length === 1) setEvidenceId(own[0].id);
    }, [open, files, evidenceId, asset?.serial_number]);

    // What the picked form says: its serial, test date and Pass/Fail.
    const readingQ = useQuery({
        queryKey: ["paper-evidence", jobId, evidenceId, asset?.asset_id],
        enabled: open && !!evidenceId && !!asset?.asset_id,
        queryFn: () =>
            api.get<EvidenceReading>(
                `/api/backflow/job/${jobId}/paper-conversion/evidence/${evidenceId}?assetId=${asset?.asset_id}`,
            ),
    });
    const reading = readingQ.data ?? null;
    useEffect(() => {
        setCoversBoth(false);
    }, [evidenceId]);
    // The date on the form, unless the office already typed one.
    useEffect(() => {
        if (reading?.formDate && !dateTouched) setPerformedOn(reading.formDate);
    }, [reading?.formDate, dateTouched]);
    const otherDevice = reading && !reading.matchesDevice ? reading.namesDevices : null;

    const convert = useMutation({
        mutationFn: () =>
            api.post<{ sentToTechId?: string | null }>(`/api/backflow/job/${jobId}/paper-conversion`, {
                assetId: asset?.asset_id,
                result,
                performedAt: dateInputToIso(performedOn),
                testerUserId: testerUserId || defaultTesterId || null,
                testerName: null,
                evidenceAttachmentId: evidenceId,
                reason: reason.trim(),
                ...(otherDevice && coversBoth ? { confirmOtherDevice: true } : {}),
                // Corbin, 10/22: the tech enters a FAIL's readings, never the
                // office. A fail goes to him; "can't file" is the last resort.
                ...(result === "fail" && cantFile ? { noReadingsCantFile: true } : {}),
            }),
        onSuccess: (data: { sentToTechId?: string | null } | undefined) => {
            toast.success(
                data?.sentToTechId
                    ? `Sent to ${techFirstName} for the readings. It shows on his iPad; no text or bell.`
                    : "Recorded from the paper — the customer's report is this device's signed form.",
            );
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["job-attachments-all", jobId] });
            qc.invalidateQueries({ queryKey: ["attachments", jobId] });
            qc.invalidateQueries({ queryKey: ["activity-timeline"] });
            setResult(null);
            setCantFile(false);
            setReason("");
            setEvidenceId("");
            setDateTouched(false);
            setPerformedOn(todayInputValue());
            onConverted?.();
            onClose();
        },
        onError: (e: unknown) => {
            const { message, error } = apiErrorInfo(e);
            toast.error(message ?? `Couldn't record the paper: ${error ?? "unknown error"}`);
        },
    });

    const dateValid = dateInputToIso(performedOn) != null;
    const signerId = testerUserId || defaultTesterId || "";
    const signer = techs.find((t) => t.id === signerId) ?? null;
    const techFirstName = signer ? techLabel(signer).split(" ")[0] : "the tech";
    const sendsToTech = result === "fail" && !cantFile;
    const canSubmit =
        (!sendsToTech || !!signerId) &&
        canRecordPaper({
            hasAsset: !!asset,
            result,
            evidenceId,
            reason,
            dateValid,
            pending: convert.isPending,
            otherDeviceOk: !otherDevice || coversBoth,
        });

    return (
        <Dialog open={open} onClose={onClose} title="Use the signed paper">
            <div className="space-y-4 px-5 py-4">
                <p className="text-sm text-foreground/80">
                    Record what the signed form says for{" "}
                    <span className="font-medium">{asset?.label ?? "this device"}</span>
                    {asset?.serial_number && <span className="font-mono"> (S/N {asset.serial_number})</span>}. The form
                    itself becomes the customer's report for this device, and the device's next-test date moves off the
                    date written on it.
                </p>
                <p className="rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    This does <span className="font-semibold">not</span> file with the city. Whoever signed the form
                    still owes the online portal entry — filing is a licensed act and this doesn't do it.
                </p>

                <div className="space-y-1.5">
                    <Label htmlFor="paper-result">What the form says *</Label>
                    <div className="flex gap-2" id="paper-result">
                        <Button
                            type="button"
                            variant={result === "pass" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setResult("pass")}
                        >
                            Passed
                        </Button>
                        <Button
                            type="button"
                            variant={result === "fail" ? "destructive" : "outline"}
                            size="sm"
                            onClick={() => setResult("fail")}
                        >
                            Failed
                        </Button>
                    </div>
                    {result === null && (
                        <p className="text-[11px] text-amber-700">Pick Passed or Failed — read it off the form.</p>
                    )}
                    {result === "fail" && (
                        <p className="text-[11px] text-foreground/60">
                            A fail records the test but leaves the device due — exactly as it really is.
                        </p>
                    )}
                    {sendsToTech && (
                        <div
                            className="space-y-1 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                            data-testid="paper-fail-to-tech"
                        >
                            <p>
                                <span className="font-semibold">{techFirstName} enters the readings.</span> The test
                                goes to his iPad with this scan next to it. He types what the paper says and saves it.
                                Until he does, the device reads <b>Fail · waiting on {techFirstName}</b> and no quote is
                                drafted.
                            </p>
                            <div className="flex justify-end">
                                <HowItWorks
                                    title="How a paper FAIL works"
                                    testId="how-paper-fail"
                                    steps={[
                                        {
                                            title: "The tech enters the test readings, not the office",
                                            text: "Attach the signed form and pick Failed. GWC sends the test to the tech who signed it, with the scan next to it.",
                                            picture: "paper-send-to-tech.png",
                                            alt: "The Use the paper box with the tech note and the Send button ringed",
                                        },
                                        {
                                            title: "Until he enters them",
                                            text: "The device reads FAIL · WAITING ON the tech, and no quote is drafted. There's nothing else for you to do.",
                                        },
                                        {
                                            title: "Paper with no numbers on it?",
                                            text: "Only then, tap the red line to record it as can't file. It shows in red, and no city report is made.",
                                            picture: "paper-cant-file.png",
                                            alt: "The red can't-file link ringed",
                                        },
                                    ]}
                                />
                            </div>
                        </div>
                    )}
                    {result === "fail" && (
                        <div className="space-y-1">
                            <button
                                type="button"
                                className="text-xs font-semibold text-red-700 underline dark:text-red-300"
                                onClick={() => setCantFile((v) => !v)}
                                data-testid="paper-cant-file-toggle"
                            >
                                {cantFile
                                    ? "Send it to the tech instead"
                                    : 'The paper has no readings. Record it as "can\'t file"'}
                            </button>
                            {cantFile && (
                                <p
                                    className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                                    data-testid="paper-cant-file-warning"
                                >
                                    Last resort. Shows in red on the job page and in Closeout, and no city report is
                                    made. Use it only when the paper really has no numbers.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="paper-date">Date on the form *</Label>
                    <Input
                        id="paper-date"
                        type="date"
                        value={performedOn}
                        max={todayInputValue()}
                        onChange={(e) => {
                            setDateTouched(true);
                            setPerformedOn(e.target.value);
                        }}
                    />
                    {reading?.formDate && (
                        <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                            The form says {formatFormDate(reading.formDate)}
                            {reading.formResult ? ` · ${reading.formResult === "pass" ? "Passed" : "Failed"}` : ""}
                        </p>
                    )}
                    <p className="text-[11px] text-foreground/60">
                        The date the test was performed, not today — the next-test date counts from it.
                    </p>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="paper-tester">Who signed it</Label>
                    <select
                        id="paper-tester"
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={testerUserId || (defaultTesterId ?? "")}
                        onChange={(e) => setTesterUserId(e.target.value)}
                    >
                        <option value="">— select the licensed tester —</option>
                        {techs.map((t) => (
                            <option key={t.id} value={t.id}>
                                {techLabel(t)}
                            </option>
                        ))}
                    </select>
                    <p className="text-[11px] text-foreground/60">
                        The result stays theirs. You're recorded as the person who entered it.
                    </p>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="paper-evidence">The signed form *</Label>
                    <select
                        id="paper-evidence"
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={evidenceId}
                        onChange={(e) => setEvidenceId(e.target.value)}
                    >
                        <option value="">— pick the file on this job —</option>
                        {files.map((f) => (
                            <option key={f.id} value={f.id}>
                                {namesSerial(f.file_name, asset?.serial_number) ? "★ " : ""}
                                {f.file_name}
                            </option>
                        ))}
                    </select>
                    {otherDevice && (
                        <div className="space-y-1.5 rounded-md border-2 border-red-500/60 bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-300">
                            <p className="font-bold">
                                This form is for S/N{" "}
                                {otherDevice
                                    .map((d) => d.serial)
                                    .filter(Boolean)
                                    .join(", ") || "another device"}
                                {asset?.serial_number ? ` — not ${asset.serial_number}` : ""}.
                            </p>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={coversBoth}
                                    onChange={(e) => setCoversBoth(e.target.checked)}
                                />
                                It really covers this device too
                            </label>
                        </div>
                    )}
                    {files.length === 0 && !filesQ.isLoading && (
                        <p className="text-[11px] text-amber-700">
                            Nothing is attached to this job yet — upload the signed form in Paperwork first.
                        </p>
                    )}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="paper-reason">Why the app didn't finish *</Label>
                    <Textarea
                        id="paper-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. app crashed mid-test, no signal in the vault, phone died"
                        rows={2}
                    />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={convert.isPending}>
                        Cancel
                    </Button>
                    <Button disabled={!canSubmit} onClick={() => convert.mutate()}>
                        {convert.isPending ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                            <FileSignature className="mr-1 h-4 w-4" />
                        )}
                        {sendsToTech
                            ? `Send to ${techFirstName} for the readings`
                            : result === "fail"
                              ? "Record it as can't file"
                              : "Record from the paper"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
